import { DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Card, Col, Modal, Row, Typography, message } from 'antd';
import { ProjectType, ProjectUsers } from 'models/Project';
import { FC, MouseEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useGetUserQuery } from 'redux/services/cookieMonster/cookieMonsterApi';
import { useUpdateProjectMutation } from 'redux/services/mrsGrouch/mrsGrouchApi';

interface Props {
  user: ProjectUsers;
  currentProject: ProjectType;
  onUserRemoved?: () => void;
}

export const UserCard: FC<Props> = ({ user, currentProject, onUserRemoved }) => {
  const { divisionId, projectId } = useParams<{ divisionId: string; projectId: string }>();
  const [updateProject, { isLoading: isRemoving }] = useUpdateProjectMutation();
  const { data: fullUser } = useGetUserQuery(user.userId, { skip: !user.userId });
  const email = fullUser?.email || user.email;

  const handleDelete = (ev: MouseEvent<HTMLElement>): void => {
    ev.stopPropagation();

    if (!divisionId || !projectId) {
      message.error('Division ID or Project ID not found');

      return;
    }

    Modal.confirm({
      title: 'Remove User from Project',
      content: `Are you sure you want to remove ${user.fullName} from this project?`,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const updatedUsers = (currentProject.users || []).filter((projectUser) => projectUser.userId !== user.userId);

          const payload = {
            customerProjectId: currentProject.customerProjectId,
            owner: currentProject.owner,
            isActive: currentProject.isActive,
            projectType: currentProject.projectType,
            status: currentProject.status,
            users: updatedUsers
          };

          await updateProject({
            divisionId,
            projectId,
            payload
          }).unwrap();

          message.success(`${user.fullName} has been removed from the project`);
          onUserRemoved?.();
        } catch (error) {
          console.error('Error removing user from project:', error);
          message.error('Failed to remove user from project. Please try again.');
        }
      }
    });
  };

  return (
    <Card
      style={{
        marginBottom: 16,
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
      bodyStyle={{ padding: 16 }}
      hoverable>
      <Row justify="space-between" align="middle">
        <Col flex="auto">
          <Row align="middle" gutter={6}>
            <Col>
              <UserOutlined style={{ marginRight: 2, color: '#1890ff' }} />
            </Col>
            <Col flex="auto">
              <Typography.Text strong style={{ fontSize: 16, display: 'block' }}>
                {user.fullName}
              </Typography.Text>
              {email && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {email}
                </Typography.Text>
              )}
            </Col>
          </Row>
        </Col>
        <Col>
          <Button type="text" size="small" icon={<DeleteOutlined />} onClick={handleDelete} style={{ color: '#ff4d4f' }} loading={isRemoving} disabled={isRemoving} />
        </Col>
      </Row>
    </Card>
  );
};
